import { Autocomplete, AutocompleteItem } from '@/components/ui';
import type { Key } from 'react';
import { useEffect, useState } from 'react';

export interface GroupsAutocompleteItem {
  id: string;
  name: string;
}

export interface GroupsAutocompleteProps {
  /** List of groups to choose from (e.g. from /pco/groups) */
  items: GroupsAutocompleteItem[];
  /** Selected group id, or empty string for none */
  value: string;
  /** Called when selection changes (id or '') */
  onChange: (id: string) => void;
  placeholder?: string;
  label?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /** When true, show a "Clear selection" link when a value is set. Default true. */
  allowClear?: boolean;
  /** When true, render the listbox in a portal so it is not clipped by overflow (e.g. inside a modal). Default false. */
  portal?: boolean;
}

/**
 * Reusable groups autocomplete: type to filter, pick one, optional clear.
 * Use with any list of { id, name } (e.g. from /pco/groups).
 */
export function GroupsAutocomplete({
  items,
  value,
  onChange,
  placeholder = 'All groups',
  label,
  className,
  'aria-label': ariaLabel,
  disabled,
  allowClear = true,
  portal = false,
}: GroupsAutocompleteProps) {
  const selectedName =
    value && items.length
      ? (items.find((g) => g.id === value)?.name ?? '')
      : '';

  const [inputValue, setInputValue] = useState(selectedName);

  useEffect(() => {
    setInputValue(selectedName);
  }, [selectedName]);

  const handleSelectionChange = (key: Key | null) => {
    const id = key ? String(key) : '';
    const item = id ? items.find((g) => g.id === id) : null;
    setInputValue(item?.name ?? '');
    onChange(id);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
  };

  return (
    <div className={className}>
      <Autocomplete
        label={label}
        placeholder={placeholder}
        items={items}
        selectedKey={value || null}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={handleSelectionChange}
        disabled={disabled}
        aria-label={ariaLabel ?? 'Select group'}
        listboxProps={{
          emptyContent:
            inputValue.trim() === ''
              ? 'Type to search groups'
              : 'No matching group',
          portal,
        }}
      >
        {(item) => (
          <AutocompleteItem key={item.id} textValue={item.name}>
            {item.name}
          </AutocompleteItem>
        )}
      </Autocomplete>
      {allowClear && value && (
        <button
          type='button'
          className='mt-1.5 text-xs text-primary hover:underline'
          onClick={handleClear}
        >
          Clear selection
        </button>
      )}
    </div>
  );
}
